import { ArrowRight, Banknote, Boxes, ClipboardCheck, PackageOpen, ShoppingBag, UsersRound } from "lucide-react";
import { Card, EmptyState, PageHeader } from "../components/ui";
import type { Customer, Order, PageKey, Product } from "../types";
import { dateTime, money, orderStatus } from "../utils";

export function DashboardPage({ orders, products, customers, onPage }: {
  orders: Order[];
  products: Product[];
  customers: Customer[];
  onPage: (page: PageKey) => void;
}) {
  const revenue = orders.filter((order) => ![8, 9].includes(order.orderStatus ?? 0)).reduce((total, order) => total + (order.totalPrice ?? 0), 0);
  const pending = orders.filter((order) => ![7, 8, 9].includes(order.orderStatus ?? 0)).length;
  const lowStock = products.filter((product) => !product.archived && product.stock <= 5).length;
  const recent = [...orders].sort((a, b) => dateValue(b.createdAt) - dateValue(a.createdAt)).slice(0, 6);

  return <>
    <PageHeader title="Добре дошли в Muse Shop" description="Преглед на магазина и задачите, които чакат внимание." action={<button className="button primary" onClick={() => onPage("products")}><ShoppingBag size={18} /> Добави продукт</button>} />
    <section className="metric-grid">
      <Metric label="Оборот от поръчки" value={money(revenue)} note="Без отказаните поръчки" icon={Banknote} tone="purple" />
      <Metric label="Активни поръчки" value={String(pending)} note="За обработка и доставка" icon={ClipboardCheck} tone="blue" />
      <Metric label="Активни продукти" value={String(products.filter((product) => product.active && !product.archived).length)} note={`${lowStock} с ниска наличност`} icon={Boxes} tone="amber" />
      <Metric label="Клиенти" value={String(customers.length)} note="Регистрирани профили" icon={UsersRound} tone="green" />
    </section>
    <section className="dashboard-grid">
      <Card title="Последни поръчки" action={<button className="text-link" onClick={() => onPage("orders")}>Всички <ArrowRight size={15} /></button>}>
        {recent.length === 0 ? <EmptyState title="Още няма поръчки" body="Новите поръчки ще се покажат тук веднага." /> : <div className="compact-orders">{recent.map((order) => {
          const status = orderStatus(order.orderStatus);
          return <button key={order.id} onClick={() => onPage("orders")}><span className="order-dot"><PackageOpen size={17} /></span><span className="grow"><strong>#{order.orderNumber || order.id.slice(0, 10)}</strong><small>{order.name || order.email || "Клиент"} · {dateTime(order.createdAt)}</small></span><span className={`badge ${status.tone}`}>{status.label}</span><strong>{money(order.totalPrice, order.currency || "BGN")}</strong></button>;
        })}</div>}
      </Card>
      <Card title="Трябва внимание">
        <div className="attention-list">
          <Attention icon={ClipboardCheck} count={orders.filter((order) => (order.orderStatus ?? 0) === 0).length} title="Нови поръчки" body="Очакват потвърждение" action={() => onPage("orders")} />
          <Attention icon={PackageOpen} count={lowStock} title="Ниска наличност" body="5 или по-малко броя" action={() => onPage("products")} />
          <Attention icon={ShoppingBag} count={products.filter((product) => product.archived).length} title="Архивирани продукти" body="Скрити от приложението" action={() => onPage("products")} />
        </div>
      </Card>
    </section>
  </>;
}

function Metric({ label, value, note, icon: Icon, tone }: { label: string; value: string; note: string; icon: typeof Banknote; tone: string }) {
  return <article className="metric-card"><div className={`metric-icon ${tone}`}><Icon size={21} /></div><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

function Attention({ icon: Icon, count, title, body, action }: { icon: typeof Banknote; count: number; title: string; body: string; action: () => void }) {
  return <button onClick={action}><span className="attention-icon"><Icon size={18} /></span><span className="grow"><strong>{title}</strong><small>{body}</small></span><b>{count}</b><ArrowRight size={16} /></button>;
}

const dateValue = (value: Order["createdAt"]) => {
  if (!value) return 0;
  if (typeof value === "object" && "toMillis" in value) return value.toMillis();
  return new Date(value as string | number | Date).getTime() || 0;
};
